import { useState } from 'react';
import { Menu, X, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface MainLayoutProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  children: React.ReactNode;
}

export default function MainLayout({ currentPath, onNavigate, children }: MainLayoutProps) {
  const { logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tempExpanded, setTempExpanded] = useState(false);
  
  const menuItems = [
    { label: 'Dashboard', path: 'dashboard' },
    { label: 'Pipeline', path: 'pipeline' },
    { label: 'Interviews', path: 'interviews' },
    { label: 'Recruiter', path: 'recruiter' },
    { label: 'Campaigns', path: 'campaigns' },
    { label: 'Plan', path: 'forecast' },
  ];

  const isActive = (path: string) => {
    return currentPath === path;
  };
  
  const handleNavigate = (path: string) => {
    onNavigate(path);
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar - Desktop: collapsible, Mobile: sliding drawer */}
      <div 
        className={`
          fixed md:static inset-y-0 left-0 z-40
          bg-white border-r border-gray-200 flex flex-col
          transform transition-all duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${sidebarCollapsed && !tempExpanded ? 'md:w-16' : 'w-64'}
        `}
        onMouseEnter={() => sidebarCollapsed && setTempExpanded(true)}
        onMouseLeave={() => setTempExpanded(false)}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h1 className={`text-2xl font-bold text-[#0156FF] transition-opacity duration-200 ${
            sidebarCollapsed && !tempExpanded ? 'md:opacity-0 md:w-0' : 'opacity-100'
          }`}>
            Recruit
          </h1>
          
          {/* Collapse button - Desktop only */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:block p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed && !tempExpanded ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
          
          {/* Close button - Mobile only */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.path}>
                <button
                  onClick={() => handleNavigate(item.path)}
                  className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={sidebarCollapsed && !tempExpanded ? item.label : undefined}
                >
                  <span className={`${
                    sidebarCollapsed && !tempExpanded ? 'md:opacity-0 md:w-0 md:hidden' : 'opacity-100'
                  }`}>
                    {item.label}
                  </span>
                  {sidebarCollapsed && !tempExpanded && (
                    <span className="hidden md:block text-center">
                      {item.label.charAt(0)}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => { logout(); window.location.href = "/login/manager"; }}
            className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            title={sidebarCollapsed && !tempExpanded ? "Log out" : undefined}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span className={`${sidebarCollapsed && !tempExpanded ? 'md:opacity-0 md:w-0 md:hidden' : 'opacity-100'}`}>
              Log out
            </span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto flex flex-col">
        {/* Mobile Header Bar */}
        <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-[#0156FF]">Recruit</h1>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
}


